"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, CheckCircle2, Clock } from "lucide-react";

export default function VerifyEmailPage() {
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const supabase = createClient();

  useEffect(() => {
    // Check if user is already logged in and email is verified
    checkEmailStatus();
  }, []);

  async function checkEmailStatus() {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user?.email_confirmed_at) {
      // Email is already verified, redirect to dashboard
      router.push('/dashboard');
    }
  }

  async function resendVerificationEmail() {
    setLoading(true);
    setError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError("No user found. Please log in again.");
        setLoading(false);
        return;
      }
      
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email as string,
      });
      
      if (error) {
        setError(error.message);
      } else {
        setEmailSent(true);
      }
    } catch (error) {
      setError("Failed to resend verification email.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">
            Check your email
          </CardTitle>
          <CardDescription className="mt-2">
            We&apos;ve sent a verification email to your address. Please check your inbox and click the link to verify.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
            <div className="flex gap-3">
              <Clock className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-primary">Didn&apos;t receive the email?</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Check your spam folder or click the button below to resend.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-destructive/10 p-3">
              <p className="text-sm text-destructive font-medium">{error}</p>
            </div>
          )}

          {emailSent && (
            <div className="rounded-lg bg-green-500/10 border border-green-500/20 p-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                <p className="text-sm font-medium text-green-700">
                  Verification email resent. Please check your inbox.
                </p>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <Button
              onClick={resendVerificationEmail}
              disabled={loading || emailSent}
              className="w-full h-12 text-base font-semibold"
              variant="outline"
            >
              {loading ? "Sending..." : emailSent ? "Email sent" : "Resend verification email"}
            </Button>

            <Button
              onClick={() => router.push('/auth/login')}
              variant="ghost"
              className="w-full h-12 text-base"
            >
              Back to login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
